import { PERF_LABEL_KIND, TraceKind, TracerStat } from './types';

const percentile = (sortedAsc: number[], p: number): number => {
  if (sortedAsc.length === 0) return 0;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const idx = Math.min(sortedAsc.length - 1, Math.floor(sortedAsc.length * p));
  return sortedAsc[idx];
};

const round1 = (value: number): number => Math.round(value * 10) / 10;

const getKind = (label: string): TraceKind =>
  (PERF_LABEL_KIND as Record<string, TraceKind | undefined>)[label] ?? 'block';

interface RawAccumulator {
  count: number;
  max: number;
  /** ring buffer */
  samples: number[];
  /** ring buffer write index */
  cursor: number;
}

const RING_BUFFER_SIZE = 256;

/**
 * 라벨별 누적기 클래스.
 * 한 인스턴스가 한 세션에 대응한다 (global / match:{matchId}).
 */
export class Tracer {
  private buckets = new Map<string, RawAccumulator>();

  public record(label: string, durationMs: number): void {
    let bucket = this.buckets.get(label);
    if (!bucket) {
      bucket = {
        count: 0,
        max: 0,
        samples: new Array(RING_BUFFER_SIZE).fill(0),
        cursor: 0,
      };
      this.buckets.set(label, bucket);
    }
    bucket.count += 1;
    if (durationMs > bucket.max) bucket.max = durationMs;
    bucket.samples[bucket.cursor] = durationMs;
    bucket.cursor = (bucket.cursor + 1) % RING_BUFFER_SIZE;
  }

  public getSummary(durationMs: number): Record<string, TracerStat> {
    const durationSec = Math.max(1, durationMs / 1000);
    const result: Record<string, TracerStat> = {};
    for (const [label, bucket] of this.buckets) {
      const filled = Math.min(bucket.count, RING_BUFFER_SIZE);
      const cursor = bucket.cursor;
      const samples =
        bucket.count <= RING_BUFFER_SIZE
          ? bucket.samples.slice(0, filled)
          : [...bucket.samples.slice(cursor), ...bucket.samples.slice(0, cursor)];
      const sorted = [...samples].sort((a, b) => a - b);
      const p50 = percentile(sorted, 0.5);
      const p95 = percentile(sorted, 0.95);
      const rate = bucket.count / durationSec;
      result[label] = {
        label,
        kind: getKind(label),
        count: bucket.count,
        rate: round1(rate),
        max: round1(bucket.max),
        p50: round1(p50),
        p95: round1(p95),
        cost: round1(rate * p50),
      };
    }
    return result;
  }

  public reset(): void {
    this.buckets.clear();
  }
}
